import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Text from "@/components/ui/text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const TRANSLATION_KEY = "settings.deals.fieldManagement.renameValueModal";

export type RenameStrategy = "replace" | "keep";

interface RenameValueModalProps {
  open: boolean;
  oldName: string;
  newName: string;
  onCancel: () => void;
  onConfirm: (strategy: RenameStrategy) => void;
}

/** Rename-in-use confirmation: "replace" renames in place, "keep" adds a new value and leaves the old. */
const RenameValueModal: React.FC<RenameValueModalProps> = ({
  open,
  oldName,
  newName,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation();
  const [strategy, setStrategy] = useState<RenameStrategy>("replace");

  return (
    <ConfirmationModal
      isOpen={open}
      setIsOpen={(value) => {
        const next = typeof value === "function" ? value(open) : value;
        if (!next) onCancel();
      }}
      data-test="rename-value-modal"
      hideDivider
      isPrimaryButton
      title={t(
        `${TRANSLATION_KEY}.title`,
        'Rename "{{oldName}}" to "{{newName}}"?',
        { oldName, newName }
      )}
      confirmationTextVariant="body"
      confirmationText={
        <div className="flex flex-col gap-4">
          {t(
            `${TRANSLATION_KEY}.body`,
            "Some records currently use this value. How should we handle them?"
          )}
          <RadioGroup
            value={strategy}
            onValueChange={(value) => setStrategy(value as RenameStrategy)}
          >
            <label className="flex items-start gap-2 cursor-pointer">
              <RadioGroupItem value="replace" className="mt-1" />
              <Text variant="body">
                {t(
                  `${TRANSLATION_KEY}.replace`,
                  'Replace "{{oldName}}" with "{{newName}}" everywhere it\'s used',
                  { oldName, newName }
                )}
              </Text>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <RadioGroupItem value="keep" className="mt-1" />
              <Text variant="body">
                {t(
                  `${TRANSLATION_KEY}.keep`,
                  'Existing records stay as "{{oldName}}". Only new entries will use the new value.',
                  { oldName }
                )}
              </Text>
            </label>
          </RadioGroup>
        </div>
      }
      submitButtonText={t(`${TRANSLATION_KEY}.confirm`, "Rename")}
      cancelButtonText={t("common.cancel", "Cancel")}
      handleSubmit={() => onConfirm(strategy)}
      handleCancel={onCancel}
    />
  );
};

export default RenameValueModal;
